import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCcw,
  FileDown,
  FileSpreadsheet,
  Sparkles,
  Database,
} from "lucide-react";
import { Button } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { ActionsCard, type ActionsCardDownloadItem } from "./ActionsCard";
import {
  download,
  getAuthorized,
  postAuthorized,
  postFormAuthorized,
} from "@/lib/fetch";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryCacheManager } from "@/hooks/useQueyCache";

type ConfigSchemaType = {
  id: number;
  code: string;
  mime_type?: string | null;
  template_file_id?: number | string | null;
  template_file_name?: string | null;
};

type ItemData = {
  provider_config_name?: string | null;
  file_name?: string | null;
};

type DataUploadActionsCardProps = {
  id: number;
  client: string;
  table_name: string;
};

type Stage = "file" | "extracted" | "transformed" | "loaded";

const STAGES: {
  key: Stage;
  label: string;
  hint: string;
  icon: React.ElementType;
  badge?: string;
}[] = [
  {
    key: "file",
    label: "Raw file",
    hint: "Original uploaded file",
    icon: FileDown,
  },
  {
    key: "extracted",
    label: "Extracted",
    hint: "Post-extract",
    icon: FileSpreadsheet,
    badge: ".csv",
  },
  {
    key: "transformed",
    label: "Transformed",
    hint: "Normalized",
    icon: Sparkles,
    badge: ".csv",
  },
  {
    key: "loaded",
    label: "Loaded",
    hint: "Loaded to database",
    icon: Database,
    badge: ".csv",
  },
];

const STAGE_ICON_COLOR: Record<Stage, string> = {
  file: "text-stone-500",
  extracted: "text-amber-500",
  transformed: "text-violet-500",
  loaded: "text-green-600",
};

export function DataUploadActionsCard({
  id,
  client,
  table_name,
}: DataUploadActionsCardProps) {
  const baseUrl = import.meta.env.VITE_BACKEND_URL as string;
  const { t } = useTranslation();
  const itemKey = [client, table_name, "get", "item", id] as const;
  const { data, refresh } = useQueryCacheManager<ItemData>(itemKey);
  const queryClient = useQueryClient();

  const [configs, setConfigs] = useState<ConfigSchemaType[]>([]);
  const [uploading, setUploading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [downloading, setDownloading] = useState<Stage | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const refreshList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [client, "audits", "get"],
    });
  }, [client, queryClient]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await getAuthorized(
          `${baseUrl}/${client}/${table_name}/configs`,
        );
        const data = await res.json();
        setConfigs(data);
      } catch (err) {
        console.error("Error fetching configs:", err);
      }
    };

    fetchConfigs();
  }, [baseUrl, client, table_name]);

  const selectedConfig = useMemo(() => {
    if (!data?.provider_config_name) return null;
    return configs.find((c) => c.code === data.provider_config_name) ?? null;
  }, [configs, data?.provider_config_name]);

  const acceptedTypes = useMemo(() => {
    if (!selectedConfig?.mime_type) return [];
    return [selectedConfig.mime_type];
  }, [selectedConfig?.mime_type]);

  const handleReload = useCallback(async () => {
    setReloading(true);
    try {
      await postAuthorized(
        `${baseUrl}/${client}/${table_name}/reload/${id}`,
        {},
      );
      await refresh();
      await refreshList();
    } catch (err) {
      console.error("Error Reloading Data:", err);
    } finally {
      setTimeout(() => setReloading(false), 250);
    }
  }, [baseUrl, client, table_name, id, refresh, refreshList]);

  const handleUploadAndReload = useCallback(async () => {
    setUploadError(null);
    if (files.length !== 1) {
      setUploadError(
        t("data_upload.errors.select_single_file", {
          defaultValue: "Please select a single file to modify.",
        }),
      );
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", files[0]);
      await postFormAuthorized(
        `${baseUrl}/${client}/${table_name}/edit/${id}`,
        formData,
      );
      await postAuthorized(
        `${baseUrl}/${client}/${table_name}/reload/${id}`,
        {},
      );
      setFiles([]);
      await refresh();
      await refreshList();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || "Upload failed.";
      setUploadError(msg);
    } finally {
      setTimeout(() => setUploading(false), 250);
    }
  }, [baseUrl, client, table_name, id, files, refresh, refreshList, t]);

  const buildDownloadUrl = useCallback(
    (stage: Stage) =>
      stage === "file"
        ? `${baseUrl}/${client}/${table_name}/download/${id}`
        : `${baseUrl}/${client}/${table_name}/download/${id}?stage=${stage}`,
    [baseUrl, client, table_name, id],
  );

  const handleDownload = useCallback(
    async (stage: Stage) => {
      setDownloading(stage);
      try {
        await download(buildDownloadUrl(stage));
      } finally {
        setDownloading(null);
      }
    },
    [buildDownloadUrl],
  );

  const downloadItems: ActionsCardDownloadItem[] = STAGES.map(
    ({ key, label, hint, icon, badge }) => ({
      key,
      label: t(`data_upload.actions.stages.${key}`, { defaultValue: label }),
      hint:
        key === "file"
          ? (data?.file_name ??
            t("data_upload.actions.hints.file", { defaultValue: hint }))
          : t(`data_upload.actions.hints.${key}`, { defaultValue: hint }),
      icon,
      badge,
      iconClassName: STAGE_ICON_COLOR[key],
      onClick: () => handleDownload(key),
      isLoading: downloading === key,
    }),
  );

  return (
    <ActionsCard
      title={t("data_upload.actions.title", { defaultValue: "Actions" })}
      primaryActions={
        <Button
          type="button"
          onPress={handleReload}
          isLoading={reloading}
          startContent={!reloading ? <RefreshCcw className="h-4 w-4" /> : null}
          radius="lg"
          variant="solid"
          color="primary"
          className="flex-1 h-10 text-sm font-medium"
        >
          {t("data_upload.actions.reload", { defaultValue: "Reload" })}
        </Button>
      }
      upload={{
        title: t("data_upload.actions.upload_reload_title", {
          defaultValue: "Upload & reload file",
        }),
        subtitle: t("data_upload.actions.upload_reload_subtitle", {
          defaultValue: "Replace file and re-run ETL",
        }),
        files,
        setFiles,
        setError: (e) => setUploadError(e ?? null),
        error: uploadError,
        onSubmit: handleUploadAndReload,
        isLoading: uploading,
        maxFiles: 1,
        acceptedTypes,
        hideStatusChips: true,
      }}
      downloads={{
        title: t("data_upload.actions.download_title", {
          defaultValue: "Download artifacts",
        }),
        subtitle: t("data_upload.actions.download_subtitle", {
          defaultValue: "Get stage outputs",
        }),
        items: downloadItems,
      }}
    />
  );
}
